/**
 * Preset layouts, copied from the design handoff (`DASH.PRESETS`).
 *
 * Widgets whose feature gate is off for the organization are dropped when a preset is
 * applied, never replaced by a placeholder. `quick` carries stable action ids, not labels.
 */
import { isWidgetVisible } from './visibility';
import { WIDGET_BY_ID } from './registry';
import { isSimplifiedUi } from '../../utils/access';

// The three legacy buttons plus Tarjousautomaatti (dropped for workshops without the
// entitlement); right alignment (the legacy placement) is the widget's default.
const KLASSINEN_QUICK = { actions: ['new_wo', 'new_quote', 'new_invoice', 'quote_machine'] };

export const PRESETS = {
  klassinen: {
    id: 'klassinen',
    role: 'admin',
    layout: [
      ['quick', 'R', KLASSINEN_QUICK],
      ['active', 'S'],
      ['pickup', 'S'],
      ['thisweek', 'S'],
      ['revenue', 'S'],
      ['weekly', 'M'],
      ['revenue6', 'M'],
      // Rows are 48 px against the legacy block's ~65 px, so LX (540 px, nine rows plus the
      // footer) shows clearly more than the five the old page capped at.
      ['active_list', 'LX'],
    ],
  },
  klassinen_emp: {
    id: 'klassinen_emp',
    role: 'employee',
    layout: [
      ['unlogged', 'R'],
      ['my_inprogress', 'W', { centered: true }],
      ['my_scheduled', 'W', { centered: true }],
      ['my_done', 'W', { centered: true }],
      ['my_list', 'LX'],
    ],
  },
  tyonjohtaja: {
    id: 'tyonjohtaja',
    role: 'admin',
    layout: [
      ['quick', 'R'],
      ['search', 'MS'],
      ['capacity', 'S'],
      ['pickup', 'S'],
      ['queue_states', 'W'],
      ['unscheduled', 'W'],
      ['uninvoiced', 'W'],
      ['timeline', 'L'],
      ['today_queue', 'M'],
      ['people', 'M'],
      ['approvals', 'M'],
      ['waiting', 'M'],
    ],
  },
  yrittaja: {
    id: 'yrittaja',
    role: 'admin',
    layout: [
      ['quick', 'R'],
      ['till', 'W'],
      ['revenue', 'W'],
      ['receivables', 'W'],
      ['revenue6', 'M'],
      ['kpis', 'M'],
      ['quotes', 'M'],
      ['uninvoiced', 'M'],
      ['due', 'M'],
      ['einvoice', 'M'],
      ['mechsales', 'M'],
      ['topcust', 'M'],
    ],
  },
  asentaja: {
    id: 'asentaja',
    role: 'employee',
    layout: [
      ['unlogged', 'R'],
      ['myclock', 'S'],
      ['my_inprogress', 'S'],
      ['my_scheduled', 'S'],
      ['my_done', 'S'],
      ['timeline', 'L', { own: true }],
      ['my_list', 'MT'],
      ['tomorrow', 'M'],
      ['pendingdays', 'MS', { self: true }],
    ],
  },
  osamyynti: {
    id: 'osamyynti',
    role: 'admin',
    layout: [
      ['quick', 'R', { actions: ['counter_sale', 'new_wo', 'new_quote', 'new_invoice', 'quote_machine'] }],
      ['till', 'W'],
      ['inbound', 'W'],
      ['suggestions', 'W'],
      ['lahetteet', 'MT'],
      ['pickups', 'MT'],
      ['lowstock', 'M'],
      ['unordered', 'M'],
    ],
  },
};

export const ADMIN_PRESET_IDS = ['klassinen', 'tyonjohtaja', 'yrittaja', 'osamyynti'];
export const EMPLOYEE_PRESET_IDS = ['klassinen_emp', 'asentaja'];

/** Presets offered to this organization's user: the own-work set for the simplified UI. */
export function presetIdsFor(organization) {
  return isSimplifiedUi(organization) ? EMPLOYEE_PRESET_IDS : ADMIN_PRESET_IDS;
}

/** `simplifiedFallback` covers the store's cached mode before the organization is known. */
export function defaultPresetId(organization, simplifiedFallback = false) {
  const simplified = organization ? isSimplifiedUi(organization) : simplifiedFallback;
  return simplified ? 'klassinen_emp' : 'klassinen';
}

/** Build a layout object from a preset for this organization (gated widgets dropped). */
export function layoutFromPreset(presetId, organization) {
  const preset = PRESETS[presetId] || PRESETS[defaultPresetId(organization)];
  const widgets = preset.layout
    .map(([id, size, opts]) => ({ id, size, opts: { ...(opts || {}) } }))
    .filter((widget) => isWidgetVisible(WIDGET_BY_ID[widget.id], organization));
  return { version: 1, preset: preset.id, widgets };
}
